<template>
    <DashboardCard
        title="Cases"
        :icon="faBallotCheck"
        :response-count="responseCount"
        action-label="View cases"
        :on-action-click="onViewCasesClick"
        :aria-busy="loading"
        :show-error="showError"
        :on-reload-click="onReloadClick"
    >
        <template #content>
            <div v-if="loading" id="cases-container">
                <div
                    v-for="skeleton in 5"
                    :key="skeleton"
                    class="case-status case-status--loading"
                >
                    <div><Skeleton width="100px" data-testid="loading" /></div>
                    <div><Skeleton width="50px" data-testid="loading" /></div>
                </div>
            </div>
            <div v-else-if="!cases || cases.all <= 0" class="empty-container">
                <div>
                    <span v-if="detractorCount > 0">You have {{ detractorCount }} detractor responses in
                        the last {{ timeFilter.label }}.</span>

                    Open your first
                    <RouterLink v-if="isMobile" to="/myfeedback">case</RouterLink>
                    <a v-else href="/responses">case</a>
                    to start tracking responses.
                </div>

                <a
                    :href="caseManagementZendeskUrl"
                    target="_blank"
                    class="find-out-link"
                >
                    Find out how Cases improve your organization's customer
                    experience.
                </a>
            </div>
            <div v-else id="cases-container" aria-label="Case management">
                <div
                    v-for="(count, status) in cases"
                    :key="status"
                    :class="['case-status', `case-status--${status}`]"
                    :aria-label="`${status}: ${count} cases`"
                >
                    <dt class="case-status__status">
                        {{ status }}
                    </dt>
                    <dd class="case-status__count">
                        {{ count }}
                    </dd>
                </div>
            </div>
        </template>
    </DashboardCard>
</template>

<script lang="ts" setup>
import { faBallotCheck } from 'fontawesome/pro-regular-svg-icons'
import DashboardCard from '@/components/FrontlineManagerDash/DashboardCard.vue'
import { TimeOptionLabel } from '@/utils/time'
import { Cases } from '@/entities/frontlineManagerDash'
import Skeleton from 'primevue/skeleton'

interface Props {
    cases: Cases | undefined
    loading: boolean
    timeFilter: TimeOptionLabel
    responseCount: number
    detractorCount: number
    isMobile: boolean
    showError?: boolean
    onReloadClick?: Function
    onViewCasesClick: Function
}

const props = defineProps<Props>()

const caseManagementZendeskUrl =
    'https://asknicely.zendesk.com/hc/en-us/articles/4401970053652-Keep-Track-Of-Your-Responses-With-Case-Management'
</script>

<style lang="less" scoped>
@import '~@/styles/prime-vue/variables.less';
@import '~@/styles/rain/variables.less';

#cases-container {
    display: flex;
    flex-direction: column;
    gap: @spacing-xs;
    margin: @spacing-2xl 0 0;
    @media (min-width: @tabletBreakpoint) {
        gap: @spacing-xl;
    }
}

.case-status {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: @fontWeight-medium;

    &__status {
        text-transform: capitalize;
        color: var(--p-gray-500);
        font-size: @fontSize-md;
        line-height: 2rem;
    }

    &__count {
        height: fit-content;
        padding: @spacing-xs @spacing-md;
        background: var(--p-gray-50);
        color: var(--p-gray-700);
        font-size: @fontSize-base;
        line-height: @fontSize-base;
        border: 1px solid var(--p-gray-200);
        border-radius: @radius-full;
        letter-spacing: @letterSpacing-md;
    }

    &--loading {
        height: @display-sm;
    }
}

.case-status--overdue {
    .case-status__status {
        color: var(--p-error-600);
    }
    .case-status__count {
        color: var(--p-error-600);
        background: var(--p-error-100);
        border-color: var(--p-error-200);
    }
}
.case-status--all {
    .case-status__status {
        color: var(--p-gray-700);
    }
}

.empty-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: @spacing-2xl;
    font-size: @fontSize-md;
    line-height: @display-sm;
    margin-top: @spacing-xl;
    font-weight: @fontWeight-medium;

    a {
        color: var(--p-primary-500);
    }

    .find-out-link {
        font-weight: @fontWeight-bold;
    }
}
</style>
